import type { GameEvent } from '../core';
import type { RunBoard } from './run';

const FEEL_REVIEW_KEY = 'playtest.feel.session';

const PANEL_FEEL_COLUMNS = [
  'session_id',
  'date',
  'device',
  'build_or_url',
  'player_cold_yes_no',
  'topic_unknown_yes_no',
  'topic',
  'first_board_completed_yes_no',
  'match_completed_yes_no',
  'teach_under_30s_yes_no',
  'risk_it_decisions',
  'total_rounds',
  'play_again_1_to_5',
  'phone_control_legibility_1_to_5',
  'rival_reads_fallible_yes_no',
  'confusion_p0_count',
  'top_issue',
  'notable_quote',
  'observer',
] as const;

type PanelFeelColumn = (typeof PANEL_FEEL_COLUMNS)[number];

interface RegistryLike {
  get(key: string): unknown;
  set(key: string, value: unknown): unknown;
}

interface FeelReviewState {
  sessionId: string;
  date: string;
  riskSignals: number;
}

export interface FeelReviewDraft {
  columns: readonly PanelFeelColumn[];
  row: Record<PanelFeelColumn, string>;
  csv: string;
  riskSignals: number;
  totalRounds: number;
}

export function isFeelReviewMode(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('review') === 'feel';
}

export function recordFeelReviewEvents(registry: RegistryLike, events: GameEvent[], seed: number): void {
  if (!isFeelReviewMode()) return;
  const riskSignals = events.filter((event) => {
    if (event.player !== 0) return false;
    return event.type === 'passArm' || event.type === 'scan' || event.type === 'gauntlet' || event.type === 'munchDecoy';
  }).length;
  if (riskSignals <= 0) {
    ensureFeelReviewState(registry, seed);
    return;
  }
  const state = ensureFeelReviewState(registry, seed);
  registry.set(FEEL_REVIEW_KEY, { ...state, riskSignals: state.riskSignals + riskSignals });
}

export function buildFeelReviewDraft(input: {
  registry: RegistryLike;
  seed: number;
  topicLabel: string;
  run: RunBoard[];
}): FeelReviewDraft | null {
  if (!isFeelReviewMode()) return null;
  const state = ensureFeelReviewState(input.registry, input.seed);
  const totalRounds = Math.max(1, input.run.length);
  const row: Record<PanelFeelColumn, string> = {
    session_id: state.sessionId,
    date: state.date,
    device: deviceLabel(),
    build_or_url: currentUrl(),
    player_cold_yes_no: '',
    topic_unknown_yes_no: '',
    topic: input.topicLabel,
    first_board_completed_yes_no: input.run.length > 0 ? 'yes' : '',
    match_completed_yes_no: input.run.length >= 5 ? 'yes' : '',
    teach_under_30s_yes_no: '',
    risk_it_decisions: String(state.riskSignals),
    total_rounds: String(totalRounds),
    play_again_1_to_5: '',
    phone_control_legibility_1_to_5: '',
    rival_reads_fallible_yes_no: '',
    confusion_p0_count: '',
    top_issue: '',
    notable_quote: '',
    observer: '',
  };
  const draft = {
    columns: PANEL_FEEL_COLUMNS,
    row,
    csv: PANEL_FEEL_COLUMNS.map((column) => csvCell(row[column])).join(','),
    riskSignals: state.riskSignals,
    totalRounds,
  };
  exposeFeelReviewDraft(draft);
  return draft;
}

export async function copyFeelReviewDraftCsv(draft: FeelReviewDraft): Promise<boolean> {
  exposeFeelReviewDraft(draft);
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(draft.csv);
      return true;
    }
  } catch {
    /* clipboard may be blocked in insecure/local browser contexts */
  }
  return false;
}

export function resetFeelReviewSession(registry: RegistryLike): void {
  registry.set(FEEL_REVIEW_KEY, null);
  exposeFeelReviewDraft(null);
}

function ensureFeelReviewState(registry: RegistryLike, seed: number): FeelReviewState {
  const existing = registry.get(FEEL_REVIEW_KEY);
  if (isFeelReviewState(existing)) return existing;
  const now = new Date();
  const date = now.toISOString().slice(0, 10);
  const state: FeelReviewState = {
    sessionId: `hf-${date.replace(/-/g, '')}-${seed}-${Date.now().toString(36)}`,
    date,
    riskSignals: 0,
  };
  registry.set(FEEL_REVIEW_KEY, state);
  return state;
}

function isFeelReviewState(value: unknown): value is FeelReviewState {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<FeelReviewState>;
  return (
    typeof candidate.sessionId === 'string' &&
    typeof candidate.date === 'string' &&
    typeof candidate.riskSignals === 'number'
  );
}

function deviceLabel(): string {
  if (typeof navigator === 'undefined') return '';
  return [navigator.platform, navigator.userAgent].filter(Boolean).join(' | ');
}

function currentUrl(): string {
  if (typeof window === 'undefined') return '';
  return window.location.href;
}

function csvCell(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function exposeFeelReviewDraft(draft: FeelReviewDraft | null): void {
  if (typeof window === 'undefined') return;
  const target = window as unknown as {
    __feelReviewDraftCsv?: string;
    __feelReviewDraft?: FeelReviewDraft | null;
  };
  if (!draft) {
    delete target.__feelReviewDraftCsv;
    target.__feelReviewDraft = null;
    return;
  }
  target.__feelReviewDraftCsv = draft.csv;
  target.__feelReviewDraft = draft;
}
